<template>
  <div v-if="stage" class="stageContainer px-3">
    <router-link :to="{ name: 'ProjectPlanEdit', params: { _id: currentProject._id }, query: $route.query }">
      <button class="modal-close-button">{{ $t('Close') }} <b-icon icon="x" /></button>
    </router-link>
    <div class="titles">
      <span class="parent" :class="{ root: !parent }">
        {{ parent }}
      </span>
      <input
        type="text"
        class="stageTitle"
        :value="stage.title"
        @change="updateSimpleProperty('title', $event.target.value)"
        :readonly="
          !editingAllowed ||
          !hasSimplePermissions('setStageProperties') ||
          (!parent && !hasSimplePermissions('editStageRootLevel'))
        "
      />
    </div>

    <div class="duration mb-3 mt-2">
      <h3>{{ startdate }} &ndash; {{ enddate }}</h3>
      {{ $t('Duration') }} {{ $t('{days} days', { days: duration }) }}
    </div>

    <div class="stageContainerMiddle">
      <div style="display: flex">
        <div class="boonCheckboxWrapper" v-if="hasPropertyPermissions('Stage', 'lockedDragNDrop')">
          <input
            class="boon-checkbox"
            id="toggleDrag"
            type="checkbox"
            v-shortkey="{ ixwin: ['ctrl', 'l'], mac: ['meta', 'l'] }"
            @shortkey="togglelock('lockedDragNDrop', !stage.lockedDragNDrop)"
            @click="togglelock('lockedDragNDrop', !stage.lockedDragNDrop)"
            :checked="stage.lockedDragNDrop"
            :disabled="!editingAllowed"
          />
          <label for="toggleDrag">{{ $t('Toggle Dragging') }}</label>
        </div>
        <div class="colorDropdown" v-if="hasPropertyPermissions('Stage', 'colour')">
          <DropDown icon="droplet-half" :title="$t('Colour')">
            <li v-for="colour in colours" :key="colour.hexcode">
              <span @click="updateColours(colour.hexcode)" :style="{ color: colour.hexcode }">
                <b-icon v-if="colour.hexcode === stage.colour" icon="droplet-fill" :style="{ color: colour.hexcode }" />
                <b-icon v-else icon="droplet-half" :title="colour.title" :style="{ color: colour.colour }" />
              </span>
            </li>
          </DropDown>
        </div>
        <button
          class="deleteStageButton"
          @click="$modal.show('confirmDeleteStageDialog', { title: 'Delete Stage?' })"
          v-if="hasSimplePermissions('deleteStage')"
        >
          <b-icon icon="trash-fill" />
        </button>
      </div>
      <div class="diary-toggle">
        <div class="boonCheckboxWrapper">
          <input
            class="boon-checkbox"
            id="toggleDiary"
            type="checkbox"
            v-shortkey="{ ixwin: ['ctrl', '2'], mac: ['meta', '2'] }"
            @shortkey="togglelock('lockedDiary', !stage.lockedDiary)"
            @click="togglelock('lockedDiary', !stage.lockedDiary)"
            v-if="hasPropertyPermissions('Stage', 'lockedDiary')"
            :checked="stage.lockedDiary"
            :disabled="!editingAllowed"
          />
          <label for="toggleDiary">{{ $t('Ej med i Diary') }}</label>
        </div>
      </div>
    </div>

    <div class="workedHours" v-if="hasSimplePermissions('viewStageWorkHours')">
      <span class="title">
        <b-icon icon="clock-history" />
        <h3>{{ $t('Work Hours') }}</h3>
      </span>
      <span class="value">
        <span class="workedHoursTotal">{{ totalWorkHours > 0 ? totalWorkHours : '-' }}</span>
      </span>
    </div>

    <b-labelled v-if="hasPropertyPermissions('Stage', 'stageManagerId')" :title="$t('Responsible Manager')">
      <multiselect
        class="boon-multiselect"
        :custom-label="(p) => personName(p._id)"
        :disabled="!hasPropertyPermissions('Stage', 'stageManagerId')"
        :multiple="false"
        :options="managingPersons"
        :placeholder="personName(managerId)"
        :value="getPerson(stage.stageManagerId)"
        @input="
          updateStagePropertyWithClear({ stage, property: 'stageManagerId', stageManagerId: $event ? $event._id : '' })
        "
        id="stageManagerId"
        track-by="_id"
      >
        <div slot="noResult">
          {{ $t('No matching person found') }}
        </div>
      </multiselect>
    </b-labelled>
    <br />
    <b-labelled v-if="hasPropertyPermissions('Stage', 'stageOccupation')" :title="$t('Area of expertise')">
      <multiselect
        class="boon-multiselect"
        :custom-label="(p) => $t(p)"
        :disabled="!hasPropertyPermissions('Stage', 'stageOccupation')"
        :multiple="false"
        :options="currentProjectRoles"
        :placeholder="$t('Choose area of expertise')"
        :value="stage.stageOccupation"
        @input="
          updateStagePropertyWithClear({ stage, property: 'stageOccupation', stageOccupation: $event ? $event : '' })
        "
        id="stageOccupation"
      >
        <div slot="noResult">
          {{ $t('No matching expertise found') }}
        </div>
      </multiselect>
    </b-labelled>

    <modal
      name="confirmDeleteStageDialog"
      classes="modalBoon"
      :clickToClose="false"
      transition="nice-modal-fade"
      :delay="100"
      :width="450"
      :height="200"
    >
      <h2>{{ $t('Delete Stage?') }}</h2>
      <p>{{ $t('Are you sure you want to delete the stage {title}?', { title: stage.title }) }}</p>
      <p>{{ $t('This action cannot be undone!') }}</p>

      <ul class="modalButtons">
        <li>
          <button class="darkButton" @click="confirmedDeleteStage()">{{ $t('Confirm') }}</button>
        </li>
        <li>
          <button class="lightButton" @click="$modal.hide('confirmDeleteStageDialog')">{{ $t('Cancel') }}</button>
        </li>
      </ul>
    </modal>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import * as stageHelpers from '@/common/stageHelpers';
import * as timeHelpers from '@/common/timeHelpers';
import * as treeHelpers from '@/common/treeHelpers';
import BLabelled from '@/components/toolbar/BLabelled';
import DropDown from '@/components/dropdown/DropDown';
import DropDownMenu from '@/components/toolbar/DropDownMenu';
import moment from 'moment';
import Multiselect from 'vue-multiselect';

export default {
  name: 'StageEdit',
  metaInfo() {
    return {
      title: this.stage.title,
    };
  },
  props: ['stage'],
  data: function () {
    return {
      confirmDeleteDialog: false,
    };
  },
  methods: {
    ...mapActions([
      'deleteStage',
      'saveStageProperties',
      'selectStage',
      'setStageProperties',
      'updateChildrenColours',
      'updateStagePropertyWithClear',
    ]),
    calculateTotalWorkHours: stageHelpers.calculateTotalWorkHours,
    togglelock: function (property, lock) {
      // console.log(property, lock);
      this.updateSimpleProperty(property, lock);
    },
    updateSimpleProperty: function (property, newValue) {
      if (this.stage[property] !== newValue) {
        var newStageProperties = {};
        newStageProperties[property] = newValue;
        // console.log(JSON.stringify(this.stage), newStageProperties);
        this.saveStageProperties({ updateStage: this.stage, newStageProperties: newStageProperties });
      }
    },
    updateColours: function (e) {
      if (e !== this.stage.colour) {
        this.updateChildrenColours({
          parent: treeHelpers.findChildren(this.currentProject.children, this.stage._id),
          stage: this.stage,
          colour: e,
        });
      }
    },
    confirmedDeleteStage: function () {
      this.$modal.hide('confirmDeleteStageDialog');
      this.deleteStage({ stage: this.stage });
    },
  },
  computed: {
    ...mapGetters([
      'colours',
      'currentProject',
      'currentProjectRoles',
      'editingAllowed',
      'fetchStage',
      'getPerson',
      'findNearestParentProperty',
      'managingPersons',
      'hasPropertyPermissions',
      'hasSimplePermissions',
      'personName',
    ]),
    managerId: function () {
      if (this.stage.stageManagerId) {
        return this.stage.stageManagerId;
      }
      return this.findNearestParentProperty(
        this.currentProject,
        this.stage._id,
        'stageManagerId',
        'developingManagerId'
      );
    },
    hoursPerWeek: function () {
      return this.duration > 0 ? timeHelpers.roundTime((7 * this.totalWorkHours) / this.duration) : '-';
    },
    totalWorkHours: function () {
      if (!this.stage) {
        return 0;
      }
      return this.calculateTotalWorkHours(this.$store, this.children);
    },
    children: function () {
      if (!this.stage) {
        return [];
      }
      return treeHelpers.findChildren(this.currentProject.children, this.stage._id);
    },
    startdate: function () {
      return stageHelpers.calculateStartdate(this.$store, '2017-01-01', this.children);
    },
    duration: function () {
      return stageHelpers.calculateTime(this.$store, '2017-01-01', this.children);
    },
    enddate: function () {
      return moment(this.startdate).add(this.duration, 'day').format('YYYY-MM-DD');
    },
    parent: function () {
      if (!this.stage) {
        return '';
      }

      const parent = treeHelpers.findParent(this.currentProject, this.stage._id);

      if (parent && parent.type.toLowerCase() !== 'project') {
        const stage = this.fetchStage(parent._id);
        return stage.title;
      }

      return '';
    },
  },
  filters: {},
  components: {
    BLabelled,
    DropDownMenu,
    Multiselect,
    DropDown,
  },
};
</script>

<style scoped>
.titles {
  padding-top: 10px;
}

.stageContainerMiddle {
  display: flex;
  flex-direction: column;
  /* justify-content: center; */
  /* align-items: center; */
  width: 100%;
}

.boonCheckboxWrapper {
  margin-right: auto;
}
.diary-toggle label {
  color: red;
}

button {
  margin: 2px;
  display: inline-flex;
  align-self: flex-end;
}

.colorDropdown {
  width: 70px;
  margin: 2px;
}

.parent:before {
  content: '> ';
}
.parent.root:before {
  content: '\a0 ';
}

.parent {
  font-size: 0.7em;
  font-weight: 600;
  color: #d67d07;
  /*    webkit-font-smoothing: subpixel-antialiased;*/
}

.stageTitle {
  font-size: 1.5em;
  font-weight: 400;
  /*text-transform: capitalize;*/
  margin-bottom: 5px;
}
.stageTitle:hover {
  outline: 1px dotted #999;
  cursor: text;
} /* TODO, unify */

.workedHours {
  display: block;
  width: 100%;
  padding: 10px 0;
}

.workedHours .title {
  display: inline-block;
  width: 70%;
}

.workedHours .title i,
.workedHours .title h3 {
  display: inline-block;
}

button.inlinebutton {
  color: black;
}

button.apparentbutton {
  background: #373e4d;
  border: 1px solid black;
  padding: 0px 2px;
  margin: 2px;
  color: white;
}

.divider {
  border-top: 2px solid black;
  display: block;
}
/* TODO: Move to GLOBAL */
input {
  max-width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/projectplan/StageEdit.vue